import Num, { BigNumber } from '@flipster/utils/num';

import { walkBracketsForLiqPrice } from './bracket-walker';
import type { InternalPosition } from './internal-types';
import { computeMaintAmounts } from './maint-amount';
import type { BracketTierWithMaintAmount } from './maint-amount';
import { toFiniteDecimal, toNonNegativeDecimal, toPositiveDecimal } from './number-guards';

export interface CalculateLiqPriceArgs {
  crossMarginBalance: string;
  maintPositionMargin: string;
  targetPosition: InternalPosition;
  tickSize: string;
  tiers: BracketTierWithMaintAmount[];
}

const findTierByNotional = (notional: BigNumber, tiers: BracketTierWithMaintAmount[]) => {
  const tier = tiers.find((entry) => Num(entry.notionalPositionSizeUpperLimit).gte(notional));
  return tier ?? tiers.at(-1) ?? null;
};

export function calculateLiqPrice(args: CalculateLiqPriceArgs): string | null {
  const position = args.targetPosition;
  const signedSize = toFiniteDecimal(position.position);
  const avgEntryPrice = toPositiveDecimal(position.avgEntryPrice);
  const markPrice = toPositiveDecimal(position.markPrice);

  if (!signedSize || signedSize.eq(0) || !avgEntryPrice || !markPrice) return null;
  if (args.tiers.length === 0) return null;

  const unrealizedCost = signedSize.multipliedBy(avgEntryPrice);
  let marginAssigned: BigNumber;

  if (position.marginType === 'Isolated') {
    const isolatedMarginInitial = toNonNegativeDecimal(position.isolatedMarginInitial);
    const isolatedMarginAdded = toNonNegativeDecimal(position.isolatedMarginAdded ?? '0');

    if (!isolatedMarginInitial || !isolatedMarginAdded) return null;

    marginAssigned = isolatedMarginInitial.plus(isolatedMarginAdded);
  } else {
    const crossMarginBalance = toFiniteDecimal(args.crossMarginBalance);
    const maintPositionMargin = toFiniteDecimal(args.maintPositionMargin);
    const walletRealization = toFiniteDecimal(position.walletRealization);

    if (!crossMarginBalance || !maintPositionMargin || !walletRealization) return null;

    const notional = signedSize.abs().multipliedBy(markPrice);
    const tier = findTierByNotional(notional, args.tiers);

    if (!tier) return null;

    const thisMaintMargin = notional.multipliedBy(tier.maintMarginRate).minus(tier.maintAmount);
    const thisPnl = signedSize.multipliedBy(markPrice.minus(avgEntryPrice));
    const effectiveBalance = crossMarginBalance.plus(walletRealization);
    const sharedBalance = effectiveBalance.minus(maintPositionMargin);
    marginAssigned = sharedBalance.plus(thisMaintMargin).minus(thisPnl);
  }

  const liqPrice = walkBracketsForLiqPrice({
    marginAssigned: marginAssigned.toString(),
    unrealizedCost: unrealizedCost.toString(),
    signedSize: position.position,
    tiers: args.tiers,
    tickSize: args.tickSize,
  });

  if (liqPrice === null) return null;

  const price = toPositiveDecimal(liqPrice);

  if (!price) return null;

  // Solution must lie on the loss side of mark price; otherwise the position
  // can't actually be liquidated from the current state and we render `—`.
  if (signedSize.gt(0) && price.gte(markPrice)) return null;
  if (signedSize.lt(0) && price.lte(markPrice)) return null;

  return liqPrice;
}

export { computeMaintAmounts };
